import Modal from 'LuxKit/Modal/Modal'
import { useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { semiFormToObject } from 'lib/forms/formUtils'
import useQueryParams from 'hooks/useQueryParams'
import Group from 'components/utils/Group'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import CruisePromotionFilter
  from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruisePromotionFilter'
import type {
  CruiseSearchPromotionChipProps,
} from 'components/Cruises/SearchPage/Filters/Inputs/CruiseSearchMobileChips/CruiseSearchPromotionChip'
import config from 'constants/config'
import { LUXURY_ESCAPES } from 'constants/brands'
import CruiseLuxPlusFilter
  from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruiseLuxPlusFilter'

const isLuxuryEscapes = config.BRAND === LUXURY_ESCAPES

function ModalCruiseSearchPromotionChip(props: CruiseSearchPromotionChipProps) {
  const {
    facets,
    filterForm,
    setFilterForm,
    fetching,
    filters,
    drawerMode,
  } = props
  const intl = useIntl()
  const modalElement = useModalElementContext<number>()
  const containerRef = useRef<HTMLDivElement>(null)
  const queryParams = useQueryParams()

  const countSelected = useMemo(() => {
    return (queryParams.get('isLuxExclusivePromotion') && isLuxuryEscapes ? 1 : 0) + (queryParams.get('isSpecialOffers') ? 1 : 0)
  }, [queryParams])

  const closeModal = useCallback(() => {
    modalElement.resolve(countSelected)
  }, [countSelected, modalElement])

  const updateForm = useCallback(() => {
    const formValues = semiFormToObject<Record<string, any>>(containerRef.current!)
    if (formValues && Object.keys(formValues).length > 0) {
      setFilterForm({
        ...filterForm,
        isLuxExclusivePromotion: formValues.isLuxExclusivePromotion,
        isSpecialOffers: formValues.isSpecialOffers,
        isLuxPlus: formValues.isLuxPlus,
      })
    } else {
      setFilterForm({
        ...filterForm,
        ...{
          isLuxExclusivePromotion: undefined,
          isSpecialOffers: undefined,
          isLuxPlus: undefined,
        },
      })
    }
  }, [filterForm, setFilterForm])

  const resetFilter = useCallback(() => {
    setFilterForm({
      ...filterForm,
      ...{
        isLuxExclusivePromotion: undefined,
        isSpecialOffers: undefined,
      },
    })
    modalElement.resolve(0)
  }, [filterForm, setFilterForm, modalElement])

  const modalTitle = intl.formatMessage({
    id: 'PkG-j1HTxTRZMVvSJt1f',
    defaultMessage: 'Promotions',
    description: 'Modal title for cruise promotions filter in the cruise section',
  })
  const applyLabel = intl.formatMessage({
    id: '9zhM4xPb52QPStIOFuuA',
    defaultMessage: 'Apply',
    description: 'Button text to apply cruise promotions filter selections',
  })
  const resetLabel = intl.formatMessage(
    {
      id: 'ndgxEcSw1skAGJeU0t6n',
      defaultMessage: '{count, plural, =0 {Reset all} one {Reset all (#)} other {Reset all (#)}}',
      description: 'Button text to reset cruise promotions filters with optional count of active filters',
    },
    { count: countSelected },
  )

  return <Modal
    mode={drawerMode ? 'drawer' : 'default'}
    ref={containerRef}
    title={modalTitle}
    primaryActionText={applyLabel}
    onPrimaryActionClick={closeModal}
    secondaryActionText={resetLabel}
    onSecondaryActionClick={resetFilter}
  >
    {fetching && <Group direction="vertical" gap={8}>
      <CheckboxInput size="large"><TextLoadingBox width="100%" /></CheckboxInput>
      <CheckboxInput size="large"><TextLoadingBox width="100%" /></CheckboxInput>
      <CheckboxInput size="large"><TextLoadingBox width="100%" /></CheckboxInput>
    </Group>}
    {!fetching && <>
      <CruiseLuxPlusFilter
        isLuxPlus={filters.isLuxPlus}
        onChange={updateForm}
        facets={facets}
      />
      <CruisePromotionFilter
        facets={facets}
        isLuxExclusivePromotion={filters.isLuxExclusivePromotion}
        isSpecialOffers={filters.isSpecialOffers}
        onChange={updateForm}
      />
    </>}
  </Modal>
}

export default ModalCruiseSearchPromotionChip
